import { Tooltip } from '@mui/material';
import {
  GridColDef,
  GridRenderCellParams,
  GridTreeNodeWithRender,
} from '@mui/x-data-grid';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store/store';
import CustomChip from '../components/CustomChip';
import { EditIcon, TrashIcon } from '../components/Icons';
import { ETheme } from '../enums/user/ETheme';
import { formatDate } from '../functions/formatDate';
import { formatPrice } from '../functions/formatPrice';
import { getExpenseTypeColor } from '../functions/getChipColors';
import { IExpense } from '../interfaces/expenses/IExpense';
import { darkTheme, lightTheme } from '../mui/theme';

export const useExpenseColumns = (
  setSelectedItem: (expense: IExpense) => void,
  setShowDeleteModal: (show: boolean) => void,
): GridColDef[] => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.auth);
  return [
    { field: 'id', headerName: t('general.id'), width: 50 },
    {
      field: 'name',
      headerName: t('expenses.name'),
      flex: 1,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{params?.row?.name ?? '-'}</div>,
    },
    {
      field: 'amount',
      headerName: t('expenses.amount'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => <div>{formatPrice(params?.row?.amount ?? '-')}</div>,
    },
    {
      field: 'createdAt',
      headerName: t('general.createdAt'),
      width: 150,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        const { createdAt } = params?.row;
        return <div>{formatDate(createdAt)}</div>;
      },
    },
    {
      field: 'expenseType',
      headerName: t('general.type'),
      width: 200,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => (
        <div>
          <CustomChip
            label={t(`expenses.type.${params?.row.expenseType}`)}
            color={getExpenseTypeColor(
              params?.row?.expenseType,
              userData?.theme,
            )}
          />
        </div>
      ),
    },
    {
      field: 'actions',
      headerName: t('general.action'),
      width: 100,
      sortable: false,
      renderCell: (
        params: GridRenderCellParams<any, any, any, GridTreeNodeWithRender>,
      ) => {
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              height: '100%',
            }}
          >
            {/* Edit or View Icon */}
            <Tooltip title={t('general.edit')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  navigate(`/admin/expenses/${params?.row?.id}`);
                }}
              >
                <EditIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>

            {/* Delete Icon */}
            <Tooltip title={t('general.delete')}>
              <span
                className={'d-flex'}
                onClick={() => {
                  setSelectedItem(params?.row);
                  setShowDeleteModal(true);
                }}
              >
                <TrashIcon
                  fill={
                    userData?.theme === ETheme.LIGHT
                      ? lightTheme.palette.text.primary
                      : darkTheme.palette.text.primary
                  }
                />
              </span>
            </Tooltip>
          </div>
        );
      },
    },
  ];
};
